import React, { useEffect, useState, useCallback } from "react";
import { NavLink } from "react-router-dom";
import ReactQuill from "react-quill";
import api from "../../api";
import Logo from "../../images/logo-2.png";
import "react-quill/dist/quill.snow.css";
import moment from "moment";
import { Loader } from "./loader";
import { useAlert } from "react-alert";

function MailToBirthdayUsers({ match }) {
  const alert = useAlert();

  const [emailContent, setEmailContent] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [birthdayUsers, setBirthdayUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [birthdayCount, setBirthdayCount] = useState(0);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  const editorModules = {
    toolbar: [
      [{ header: "1" }, { header: "2" }, { font: [] }],
      [{ size: [] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [
        { list: "ordered" },
        { list: "bullet" },
        { indent: "-1" },
        { indent: "+1" },
      ],
      ["link", "image", "video"],
      ["clean"],
    ],
    clipboard: {
      matchVisual: false,
    },
  };

  const fetchBirthdayUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await api.getUserByBirthday();

      if (response?.success) {
        const users = response.data || [];
        setBirthdayUsers(users);
        setBirthdayCount(users.length);
      } else {
        setError("Failed to fetch birthday users");
      }
    } catch (error) {
      console.error("Error fetching birthday users:", error);
      setError(error?.message || "Failed to load birthday users");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleSendBirthdayWishes = async () => {
    if (birthdayCount === 0) {
      alert.info("No users with birthdays today");
      return;
    }

    if (!window.confirm(`Send birthday wishes to ${birthdayCount} user(s)?`)) {
      return;
    }

    try {
      setIsLoading(true);
      setSuccessMessage("");
      setError(null);

      const response = await api.sendMailTobirthdayUser({
        subject: emailSubject,
        content: emailContent,
      });

      if (response.success) {
        setSuccessMessage(
          response.message || "Birthday wishes sent successfully!",
        );
        alert.success(response.message || "Birthday wishes sent successfully!");

        // Refresh the list after sending
        await fetchBirthdayUsers();
      } else {
        throw new Error(response.message || "Failed to send birthday wishes");
      }
    } catch (error) {
      console.error("Error sending birthday wishes:", error);
      const errorMsg = error?.message || "Something went wrong, email not sent";
      setError(errorMsg);
      alert.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBirthdayUsers();
  }, [fetchBirthdayUsers]);

  return (
    <div className="container-fluid birthday-wishes-page">
      {/* Success Message Banner */}
      {successMessage && (
        <div
          className="alert alert-success alert-dismissible fade show birthday-alert"
          role="alert"
        >
          {successMessage}
          <button
            type="button"
            className="close"
            onClick={() => setSuccessMessage("")}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      )}

      {/* Error Message Banner */}
      {error && (
        <div
          className="alert alert-danger alert-dismissible fade show birthday-alert"
          role="alert"
        >
          {error}
          <button
            type="button"
            className="close"
            onClick={() => setError(null)}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      )}

      <div className="row">
        <div className="col">
          {/* Header Section */}
          <div className="row mx-0 birthday-header-row">
            <div className="col-xl-6 col-md-7 d-flex justify-content-between pr-0">
              <h4 className="color-dark fs-17 dib font-weight-bold mr-5">
                Send Birthday Wish To User ({birthdayCount})
              </h4>
              <span className="badge badge-pill badge-birthday">
                {birthdayCount} {birthdayCount === 1 ? "Birthday" : "Birthdays"}{" "}
                Today
              </span>
            </div>
          </div>

          {/* Email Editor Card */}
          {birthdayCount > 0 && (
            <div className="card birthday-editor-card br-10 border-transparent mt-3">
              <div className="card-header br-10 bg-white border-transparent py-3">
                <h5 className="card-title birthday-card-title">
                  Birthday Email Template
                </h5>
              </div>
              <div className="card-body">
                <div className="form-group birthday-form-group">
                  <label htmlFor="emailSubject" className="birthday-label">
                    Subject
                  </label>
                  <input
                    type="text"
                    id="emailSubject"
                    className="form-control birthday-input"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="Enter email subject..."
                  />
                </div>
                <div className="form-group birthday-form-group">
                  <label htmlFor="emailContent" className="birthday-label">
                    Message
                  </label>
                  <ReactQuill
                    theme="snow"
                    value={emailContent}
                    onChange={setEmailContent}
                    modules={editorModules}
                    className="birthday-quill-editor"
                    placeholder="Compose your birthday message..."
                  />
                </div>
              </div>
            </div>
          )}

          {/* Action Card */}
          <div className="card birthday-action-card br-10 border-transparent mt-3">
            <div className="card-header br-10 bg-white border-transparent py-3">
              {birthdayCount > 0 ? (
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="mb-0 birthday-action-text">
                      Ready to send birthday wishes to {birthdayCount} user(s)
                    </p>
                  </div>
                  <div className="birthday-action-buttons">
                    <button
                      onClick={handleSendBirthdayWishes}
                      className="btn btn-primary btn-birthday-send"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm mr-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Sending...
                        </>
                      ) : (
                        "Send Birthday Wishes"
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center birthday-no-users">
                  <p className="mb-0">No birthdays today!</p>
                </div>
              )}
            </div>
          </div>

          {/* Birthday Users Table - Always shows table header */}
          <BirthdayUsersTable users={birthdayUsers} isLoading={isLoading} />
        </div>
      </div>

      {/* Loading Overlay */}
      {isLoading && (
        <Loader />
        // <div className="birthday-loader-overlay">
        //   <div className="birthday-loader">
        //     <img src={Logo} alt="Loading..." className="birthday-loader-image" />
        //     <p className="birthday-loader-text">Loading birthday users...</p>
        //   </div>
        // </div>
      )}
    </div>
  );
}

const BirthdayUsersTable = ({ users, isLoading }) => {
  const formatBirthdayDate = (date) => {
    if (!date) return "N/A";

    try {
      return moment(date).format("MMMM Do YYYY");
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid Date";
    }
  };

  // Show loading state only when first loading
  if (isLoading && users.length === 0) {
    return (
      <div className="card birthday-table-card br-10 border-transparent mt-3">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover birthday-table">
              <thead className="birthday-table-header">
                <tr>
                  <th scope="col" className="birthday-table-head">
                    Name
                  </th>
                  <th scope="col" className="birthday-table-head">
                    Email
                  </th>
                  <th scope="col" className="birthday-table-head">
                    Date of Birth
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="birthday-table-row-empty">
                  <td colSpan="3" className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                    <p className="mt-3 mb-0">Loading birthday users...</p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card birthday-table-card br-10 border-transparent mt-3">
      <div className="card-header br-10 bg-white border-transparent py-3">
        <h5 className="card-title birthday-table-title">
          {users.length > 0 ? "Today's Birthday Users" : "Birthday Users"}
        </h5>
      </div>
      <div className="card-body p-0">
        <div className="table-responsive">
          <table className="table table-hover birthday-table">
            <thead className="birthday-table-header">
              <tr>
                <th scope="col" className="birthday-table-head">
                  Name
                </th>
                <th scope="col" className="birthday-table-head">
                  Email
                </th>
                <th scope="col" className="birthday-table-head">
                  Date of Birth
                </th>
              </tr>
            </thead>
            <tbody>
              {users.length > 0 ? (
                users.map((user, index) => (
                  <tr key={user.id || index} className="birthday-table-row">
                    <td className="birthday-table-cell birthday-user-name">
                      <div className="d-flex align-items-center">
                        <div className="birthday-user-avatar">
                          {user.name?.charAt(0) || "U"}
                        </div>
                        <span className="ml-2">{user.name || "N/A"}</span>
                      </div>
                    </td>
                    <td className="birthday-table-cell birthday-user-email">
                      {user.email || "N/A"}
                    </td>
                    <td className="birthday-table-cell birthday-user-dob">
                      <div className="d-flex align-items-center">
                        <i className="fas fa-birthday-cake text-primary me-2"></i>
                        <div className="birthday-date">
                          <div className="birthday-full-date">
                            {formatBirthdayDate(user.dob)}
                          </div>
                          {user.dob && (
                            <small className="text-muted birthday-date-ago">
                              {moment(user.dob).fromNow()}
                            </small>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                // Empty state row - shows message but table header remains
                <tr className="birthday-table-row-empty">
                  <td colSpan="3" className="text-center py-5">
                    <div className="birthday-empty-state">
                      <i className="fas fa-birthday-cake fa-3x mb-3 text-muted birthday-empty-icon"></i>
                      <h5 className="birthday-empty-title">
                        No Birthdays Today
                      </h5>
                      <p className="text-muted birthday-empty-text">
                        There are no users celebrating their birthday today.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MailToBirthdayUsers;
